import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import { MailCheck } from "lucide-react";
import { ROLE_HOME } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import api from "../lib/api";
import { Logo } from "../components/Shared";
import { isNative } from "../native";

const TS_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState("");

  const startMfaSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { setup_token: mfa.token });
      setQr(data.qr_data_url);
      setManualKey(data.manual_key);
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(mfa.setup ? "/auth/mfa/enable" : "/auth/mfa/verify",
        mfa.setup ? { setup_token: mfa.token, code: mfaCode } : { mfa_token: mfa.token, code: mfaCode });
      toast.success(mfa.setup ? "MFA enabled. Welcome!" : "Welcome back!");
      window.location.assign(ROLE_HOME[data.user.role] || "/app/family");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        if (data.mfa_required) { setMfa({ token: data.mfa_token, setup: false }); setBusy(false); return; }
        if (data.mfa_setup_required) { setMfa({ token: data.setup_token, setup: true }); setBusy(false); return; }
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
        window.location.assign(loc.state?.from || ROLE_HOME[data.user.role] || "/app/family");
      } else {
        if (TS_KEY && !turnstileToken) { toast.error("Please complete the security check."); setBusy(false); return; }
        await api.post("/auth/register", { ...form, turnstile_token: turnstileToken });
        setRegistered(true);
      }
    } catch (err) {
      if (err?.response?.data?.detail === "email_not_verified") setUnverifiedEmail(form.email.trim().toLowerCase());
      else toast.error(formatErr(err));
    }
    setBusy(false);
  };

  return (
    <div className="p1-container flex justify-center py-14 lg:py-20" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="p1-card p-8">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-5 text-center font-heading text-2xl font-bold text-stone-900">
            {mode === "login" ? "Welcome back" : "Create your family account"}
          </h1>
          <p className="mt-1 text-center text-sm text-stone-500">
            {mode === "login" ? "Sign in to see how your parents are doing." : "Then tell us about your parents."}
          </p>
          {mfa ? (
            <div className="mt-6" data-testid="mfa-panel">
              <h2 className="text-center font-heading text-xl font-bold text-stone-900">
                {mfa.setup ? "Set up two-factor authentication" : "Two-factor authentication"}
              </h2>
              <p className="mt-1 text-center text-sm text-stone-500">
                {mfa.setup
                  ? "Staff accounts require MFA. Scan the QR code with Google Authenticator, Authy or any authenticator app."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
              {mfa.setup && !qr && (
                <button onClick={startMfaSetup} disabled={busy} className="p1-btn-primary mt-5 w-full" data-testid="mfa-show-qr">
                  {busy ? "Generating…" : "Show my QR code"}
                </button>
              )}
              {qr && (
                <div className="mt-5 text-center">
                  <img src={qr} alt="MFA QR code" className="mx-auto h-48 w-48 rounded-xl border border-stone-200" data-testid="mfa-qr" />
                  <p className="mt-3 text-xs text-stone-500">Can't scan? Enter this key manually in your authenticator app:</p>
                  <p className="mt-1 select-all rounded-lg bg-stone-100 px-3 py-2 font-mono text-sm tracking-widest text-stone-800" data-testid="mfa-manual-key">{manualKey}</p>
                </div>
              )}
              {(qr || !mfa.setup) && (
                <form onSubmit={submitMfa} className="mt-5 space-y-4" data-testid="mfa-form">
                  <input inputMode="numeric" maxLength={6} className="p1-input text-center text-2xl font-bold tracking-[0.5em]" placeholder="••••••"
                    value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))} data-testid="mfa-code-input" />
                  <button type="submit" disabled={busy || mfaCode.length !== 6} className="p1-btn-primary w-full disabled:opacity-40" data-testid="mfa-submit">
                    {busy ? "Verifying…" : mfa.setup ? "Enable MFA & Sign In" : "Verify & Sign In"}
                  </button>
                </form>
              )}
              <button onClick={() => { setMfa(null); setQr(null); setMfaCode(""); }} className="mt-3 w-full text-center text-xs font-semibold text-stone-400 hover:text-stone-600" data-testid="mfa-back">← Back to login</button>
            </div>
          ) : registered ? (
            <div className="mt-6 text-center" data-testid="check-email-panel">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><MailCheck className="h-7 w-7 text-emerald-800" /></div>
              <h2 className="mt-4 font-heading text-xl font-bold text-stone-900">Check your email</h2>
              <p className="mt-2 text-sm leading-relaxed text-stone-600">We've sent a verification link to <strong>{form.email}</strong>. Click it within 24 hours to activate your account and continue to onboarding.</p>
              <button onClick={resend} disabled={busy} className="mt-4 text-sm font-semibold text-emerald-900 hover:underline" data-testid="resend-verification-btn">
                {busy ? "Sending…" : "Didn't get it? Resend verification email"}
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div><label className="p1-label">Full name *</label>
                <input className="p1-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" /></div>
            )}
            <div><label className="p1-label">Email *</label>
              <input type="email" className="p1-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" /></div>
            {mode === "register" && (
              <div><label className="p1-label">Mobile number (WhatsApp/SMS care alerts) *</label>
                <input className="p1-input" required placeholder="+91 98XXXXXXXX" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="auth-phone" /></div>
            )}
            <div><label className="p1-label">Password *</label>
              <input type="password" className="p1-input" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" /></div>
            {mode === "login" && (
              <p className="text-right"><Link to="/forgot-password" className="text-xs font-semibold text-emerald-900 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></p>
            )}
            {mode === "register" && (
              TS_KEY ? (
                <div className="flex justify-center" data-testid="turnstile-widget">
                  <Turnstile siteKey={TS_KEY} onSuccess={(t) => setTurnstileToken(t)} onExpire={() => setTurnstileToken(null)} onError={() => setTurnstileToken(null)}
                    options={{ appearance: "interaction-only", theme: "light", size: "flexible" }} />
                </div>
              ) : (
                <p className="rounded-lg bg-stone-100 p-2 text-center text-xs text-stone-500">Security check activates when bot protection is configured.</p>
              )
            )}
            <button type="submit" disabled={busy} className="p1-btn-primary w-full" data-testid="auth-submit">
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
          )}
          {unverifiedEmail && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="unverified-panel">
              <strong>Email not verified.</strong> Please verify <span className="font-semibold">{unverifiedEmail}</span> to continue.
              <button onClick={resend} disabled={busy} className="mt-2 block font-semibold text-emerald-900 underline" data-testid="resend-unverified-btn">Resend verification email</button>
            </div>
          )}
          <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setRegistered(false); setUnverifiedEmail(""); }}
            className="mt-4 w-full text-center text-sm font-semibold text-emerald-900 hover:underline" data-testid="auth-toggle-mode">
            {mode === "login" ? "New to ParentOne? Create an account" : "Already have an account? Sign in"}
          </button>
        </div>
        {!isNative() && <p className="mt-4 text-center text-xs text-stone-400"><Link to="/" className="hover:text-stone-600">← Back to parentone.in</Link></p>}
      </div>
    </div>
  );
}
